import type {
  DifyAPITestJob,
  DifyAPITestJobStatus,
  DifyAPITestRun,
  DifyAPITestRunStatus,
} from "~/components/difyAPITest/difyAPITest.types";

type RunnerState = {
  runs: DifyAPITestRun[];
  activeRunId: string | null;
};

/**
 * DifyAPIテスト実行状態更新用のアクション
 * RUN_ENQUEUED: 実行情報を履歴へ追加
 * JOB_STATUS_CHANGED: ジョブ状態を更新
 * JOB_CHUNK_RECEIVED: ジョブの受信チャンクを追加
 * JOB_COMPLETED: ジョブを正常完了へ更新
 * JOB_FAILED: ジョブを異常終了へ更新
 * RUN_CANCEL_REQUESTED: 実行全体をキャンセル状態へ更新
 * RUN_FINISHED: 実行全体の終了状態を更新
 * CLEAR_RUNS: 実行履歴を全件クリア
 */
type RunnerAction =
  | { type: "RUN_ENQUEUED"; run: DifyAPITestRun }
  | { type: "JOB_STATUS_CHANGED"; runId: string; jobId: string; status: DifyAPITestJobStatus }
  | { type: "JOB_CHUNK_RECEIVED"; runId: string; jobId: string; chunk: string }
  | { type: "JOB_COMPLETED"; runId: string; jobId: string; messageId: string }
  | { type: "JOB_FAILED"; runId: string; jobId: string; errorMessage: string }
  | { type: "RUN_CANCEL_REQUESTED"; runId: string }
  | { type: "RUN_FINISHED"; runId: string; status: DifyAPITestRunStatus }
  | { type: "CLEAR_RUNS" };

/**
 * ジョブ状態が終了済みかどうかを判定する
 * @param status ジョブ状態
 * @return 終了済みかどうか
 */
const isTerminalJobStatus = (status: DifyAPITestJobStatus) => {
  return status === "completed" || status === "error" || status === "cancelled";
};

/**
 * 指定した実行情報を更新する
 * @param state 現在の状態
 * @param runId 実行 ID
 * @param updater 実行情報の更新関数
 * @return 更新後の状態
 */
const updateRun = (
  state: RunnerState,
  runId: string,
  updater: (run: DifyAPITestRun) => DifyAPITestRun
) => {
  return {
    ...state,
    runs: state.runs.map((run) => (run.runId === runId ? updater(run) : run)),
  };
};

/**
 * 指定したジョブ情報を更新する
 * @param state 現在の状態
 * @param runId 実行 ID
 * @param jobId ジョブ ID
 * @param updater ジョブ情報の更新関数
 * @return 更新後の状態
 */
const updateJob = (
  state: RunnerState,
  runId: string,
  jobId: string,
  updater: (job: DifyAPITestJob) => DifyAPITestJob
) => {
  return updateRun(state, runId, (run) => ({
    ...run,
    jobs: run.jobs.map((job) => (job.jobId === jobId ? updater(job) : job)),
  }));
};

export const initialRunnerState: RunnerState = {
  runs: [],
  activeRunId: null,
};

/**
 * DifyAPIテスト実行状態を更新する
 * @param state 現在の状態
 * @param action 実行するアクション
 * @return 更新後の状態
 */
export const difyAPITestRunnerReducer = (state: RunnerState, action: RunnerAction): RunnerState => {
  switch (action.type) {
    case "RUN_ENQUEUED":
      return {
        ...state,
        runs: [...state.runs, action.run],
        activeRunId: action.run.runId,
      };

    case "JOB_STATUS_CHANGED":
      return updateJob(state, action.runId, action.jobId, (job) => {
        if (isTerminalJobStatus(job.status)) {
          return job;
        }

        return {
          ...job,
          status: action.status,
          startedAt: job.startedAt ?? new Date(),
          errorMessage: action.status === "running" ? "" : job.errorMessage,
        };
      });

    case "JOB_CHUNK_RECEIVED":
      return updateJob(state, action.runId, action.jobId, (job) => {
        if (isTerminalJobStatus(job.status)) {
          return job;
        }

        return {
          ...job,
          status: "running",
          chunks: [...job.chunks, action.chunk],
          aiOutput: `${job.aiOutput}${action.chunk}`,
        };
      });

    case "JOB_COMPLETED":
      return updateJob(state, action.runId, action.jobId, (job) => {
        if (job.status === "cancelled") {
          return job;
        }

        return {
          ...job,
          status: "completed",
          messageId: action.messageId,
          completedAt: new Date(),
        };
      });

    case "JOB_FAILED":
      return updateJob(state, action.runId, action.jobId, (job) => {
        if (job.status === "cancelled" || job.status === "completed") {
          return job;
        }

        return {
          ...job,
          status: "error",
          errorMessage: action.errorMessage,
          completedAt: new Date(),
        };
      });

    case "RUN_CANCEL_REQUESTED": {
      const currentDate = new Date();

      return updateRun(state, action.runId, (run) => ({
        ...run,
        status: "cancelled",
        finishedAt: run.finishedAt ?? currentDate,
        jobs: run.jobs.map((job) => {
          if (job.status === "queued" || job.status === "running") {
            return {
              ...job,
              status: "cancelled",
              errorMessage: job.errorMessage || "キャンセルしました。",
              completedAt: job.completedAt ?? currentDate,
            };
          }

          return job;
        }),
      }));
    }

    case "RUN_FINISHED":
      return updateRun(state, action.runId, (run) => ({
        ...run,
        status: run.status === "cancelled" ? "cancelled" : action.status,
        finishedAt: run.finishedAt ?? new Date(),
      }));

    case "CLEAR_RUNS":
      return initialRunnerState;

    default:
      return state;
  }
};

export type { RunnerAction, RunnerState };
